import type { UseMutationResult } from "@tanstack/react-query";

import { useMemo } from "react";
// Removed imports for toast and useTranslation as we now handle notifications at the component level

import type { ApiResponse } from "./types";

// Import from API mutations directly
import { useCreateRecording, useUpdateRecordingMeta } from "../../../../../api/mutations";

// Types for recording operations
export type CreateRecordingData = {
  title: string;
  performingartist: string;
  isrc?: string;
  releasedate?: string;
  workcode: string;
  etag: string;
  spotifyidentifier?: string | null;
  spotifypopularity?: number | null;
};

export type UpdateRecordingMetaData = {
  id: string;
  spotify_id?: string | null;
  mp3?: string | null;
  thumb_url?: string | null;
  song_id: string;
};

export type RecordingResponse = {
  recordingidentifier: string;
  [key: string]: unknown;
};

type RecordingOperationsOptions = {
  selectedAccount: string;
  onSuccess?: (data: RecordingResponse) => void;
  onValidationError?: (messages: string[]) => void;
  onError?: (message: string) => void;
};

type RecordingOperationsResult = {
  createRecording: (recordingData: CreateRecordingData) => Promise<RecordingResponse>;
  updateRecordingMeta: (metaData: UpdateRecordingMetaData) => Promise<ApiResponse>;
  isCreatingRecording: boolean;
  isUpdatingMeta: boolean;
  isLoading: boolean;
};

// Define mutation hooks as proper types with generics
type CreateRecordingMutationHook = UseMutationResult<
  ApiResponse<RecordingResponse>,
  Error,
  Record<string, unknown>,
  unknown
>;

type UpdateRecordingMetaMutationHook = UseMutationResult<
  ApiResponse,
  Error,
  Record<string, unknown>,
  unknown
>;

// Character map for sanitization
const charMap: Record<string, string> = {
  Þ: "TH",
  þ: "th",
  ß: "ss",
  æ: "ae",
  Æ: "AE",
  ø: "o",
  Ø: "O",
  ð: "d",
  Ð: "D",
};

/**
 * Custom hook for managing recording operations
 */
export function useRecordingOperations({
  selectedAccount,
  onSuccess = () => {},
  onValidationError = () => {},
  onError = () => {},
}: RecordingOperationsOptions): RecordingOperationsResult {
  // Removed useTranslation as we don't need it anymore for toasts

  // Cast the mutation hooks to their proper types
  // Use any as an intermediate step to avoid TypeScript errors (cleaner than Function)
  const recordingCreateMutation = (useCreateRecording() as unknown) as CreateRecordingMutationHook;
  const recordingMetaMutation = (useUpdateRecordingMeta() as unknown) as UpdateRecordingMetaMutationHook;

  // Create our own sanitize function with the requested logic
  const sanitizeInput = (input: string): string => {
    if (typeof input !== "string")
      return String(input);

    return input
      .normalize("NFD") // Decompose accented characters
      .replace(/[\u0300-\u036F]/g, "") // Remove diacritical marks
      .replace(/[Þßæøð]/gi, char => charMap[char] || char) // Replace special characters
      .replace(/[^a-z0-9 !"#$%&'()*+,\-./:;<=>?@[\\\]^`{}~£€]/gi, ""); // Keep only allowed chars
  };

  const operations = useMemo(() => ({
    /**
     * Create a new recording
     */
    createRecording: async (recordingData: CreateRecordingData): Promise<RecordingResponse> => {
      // Only reset if it exists
      if (typeof recordingCreateMutation.reset === "function") {
        recordingCreateMutation.reset();
      }

      const {
        title,
        performingartist,
        isrc,
        releasedate,
        workcode,
        etag,
        spotifyidentifier,
        spotifypopularity,
      } = recordingData;

      // Safely sanitize inputs
      const sanitizedTitle = sanitizeInput(String(title));
      const sanitizedArtist = sanitizeInput(String(performingartist));

      const data = {
        artistaccountcode: selectedAccount,
        title: sanitizedTitle,
        performingartist: sanitizedArtist,
        isrc,
        releasedate,
        workcode,
        spotifyidentifier: spotifyidentifier === undefined || spotifyidentifier === null
          ? null
          : spotifyidentifier,
        spotifypopularity: spotifyidentifier === undefined || spotifyidentifier === null
          ? null
          : spotifypopularity,
        spotifypopularitylastupdatedutc: spotifyidentifier === undefined || spotifyidentifier === null
          ? null
          : new Date().toISOString(),
        etag,
      };

      return new Promise<RecordingResponse>((resolve, reject) => {
        recordingCreateMutation.mutate(data, {
          onSuccess: (response) => {
            // Check if validation failed with improved type guards using optional chaining
            if (response?.failedvalidation) {
              const validationMessages = response.messages || ["Validation failed"];
              onValidationError(validationMessages);
              reject(new Error("Validation failed"));
            }
            else {
              // Handle successful creation
              const result = response?.model as RecordingResponse;
              onSuccess(result);
              resolve(result);
            }
          },
          onError: (err: Error) => {
            const errorMessage = err?.message || "Unknown error";
            onError(errorMessage);
            reject(err);
          },
        });
      });
    },

    /**
     * Update recording metadata
     */
    updateRecordingMeta: async (metaData: UpdateRecordingMetaData): Promise<ApiResponse> => {
      // Only reset if it exists
      if (typeof recordingMetaMutation.reset === "function") {
        recordingMetaMutation.reset();
      }

      return new Promise<ApiResponse>((resolve, reject) => {
        recordingMetaMutation.mutate(metaData, {
          onSuccess: (response) => {
            // Check if validation failed with improved type guards using optional chaining
            if (response?.failedvalidation) {
              const validationMessages = response.messages || ["Validation failed"];
              onValidationError(validationMessages);
              reject(new Error("Validation failed"));
            }
            else {
              // Removed toast notification as it's now handled at the component level
              onSuccess(response.model as RecordingResponse);
              resolve(response);
            }
          },
          onError: (err: Error) => {
            const errorMessage = err?.message || "Unknown error";
            onError(errorMessage);
            reject(err);
          },
        });
      });
    },
  }), [recordingCreateMutation, recordingMetaMutation, selectedAccount, onSuccess, onValidationError, onError]);

  // Return operations and loading states
  return {
    ...operations,
    isCreatingRecording: Boolean(recordingCreateMutation?.isLoading),
    isUpdatingMeta: Boolean(recordingMetaMutation?.isLoading),
    isLoading: Boolean(recordingCreateMutation?.isLoading || recordingMetaMutation?.isLoading),
  };
}
